import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Tabs,
  Tab,
  Paper,
  Card,
  CardContent,
  CardActions,
  Button,
  TextField,
  FormControlLabel,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Grid,
  Typography,
  Chip,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon,
  Download as DownloadIcon,
  GitHub as GitHubForkIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
} from "@mui/icons-material";
import { useAuth } from "../hooks/useAuth";
import { FrameworkService } from "../services/FrameworkService";
import { DevelopmentFramework } from "../types/framework";
import APP_AGENT_FRAMEWORK_CONTENT from "../utils/framework-content";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`framework-tabpanel-${index}`}
    aria-labelledby={`framework-tab-${index}`}
  >
    {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
  </div>
);

const DevelopmentFrameworkPage: React.FC = () => {
  const { user } = useAuth();
  const [tabValue, setTabValue] = useState(0);
  const [frameworks, setFrameworks] = useState<DevelopmentFramework[]>([]);
  const [selectedFramework, setSelectedFramework] =
    useState<DevelopmentFramework | null>(null);
  const [publicFrameworks, setPublicFrameworks] = useState<
    DevelopmentFramework[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [newFrameworkDialog, setNewFrameworkDialog] = useState(false);
  const [forkDialog, setForkDialog] = useState(false);
  const [selectedForFork, setSelectedForFork] =
    useState<DevelopmentFramework | null>(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [selectedForDelete, setSelectedForDelete] =
    useState<DevelopmentFramework | null>(null);

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    isPublic: false,
    tags: "",
  });

  // Load default framework on mount
  useEffect(() => {
    loadDefaultFramework();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (user?.email) {
      loadUserFrameworks();
      loadPublicFrameworks();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.email]);

  const loadDefaultFramework = useCallback(async () => {
    try {
      setSelectedFramework({
        frameworkId: "default",
        userId: "system",
        version: "1.0.0",
        title: "App Agent Development Framework",
        description:
          "Comprehensive framework for initializing AI agents to build new applications from scratch",
        content: APP_AGENT_FRAMEWORK_CONTENT,
        techStacks: [],
        architecturePatterns: [],
        codeTemplates: [],
        workflowSteps: [],
        bestPractices: [],
        createdAt: new Date(),
        updatedAt: new Date(),
        isPublic: true,
        forks: 0,
        tags: ["framework", "ai-agent", "development", "boilerplate"],
      } as DevelopmentFramework);
    } catch (err) {
      console.error("Error loading default framework:", err);
    }
  }, []);

  const loadUserFrameworks = useCallback(async () => {
    if (!user?.email) return;
    try {
      setLoading(true);
      const userFrameworks = await FrameworkService.getUserFrameworks(
        user.email,
      );
      setFrameworks(userFrameworks);
    } catch (err) {
      setError("Failed to load frameworks");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [user?.email]);

  const loadPublicFrameworks = useCallback(async () => {
    try {
      setLoading(true);
      const pubFrameworks = await FrameworkService.getPublicFrameworks();
      setPublicFrameworks(pubFrameworks);
    } catch (err) {
      console.error("Error loading public frameworks:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleExportAsMarkdown = () => {
    if (!selectedFramework) return;
    const content = FrameworkService.exportAsMarkdown(selectedFramework);
    const element = document.createElement("a");
    element.setAttribute(
      "href",
      "data:text/markdown;charset=utf-8," + encodeURIComponent(content),
    );
    element.setAttribute("download", `${selectedFramework.title}.md`);
    element.style.display = "none";
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  const handleExportAsJSON = () => {
    if (!selectedFramework) return;
    const content = FrameworkService.exportAsJSON(selectedFramework);
    const element = document.createElement("a");
    element.setAttribute(
      "href",
      "data:application/json;charset=utf-8," + encodeURIComponent(content),
    );
    element.setAttribute("download", `${selectedFramework.title}.json`);
    element.style.display = "none";
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  const handleFork = async (framework: DevelopmentFramework) => {
    if (!user?.email) return;
    try {
      setLoading(true);
      await FrameworkService.forkFramework(user.email, framework.frameworkId);
      await loadUserFrameworks();
      setError(null);
      alert("Framework forked successfully!");
    } catch (err) {
      setError("Failed to fork framework");
      console.error(err);
    } finally {
      setLoading(false);
      setForkDialog(false);
    }
  };

  const handleCreateFramework = async () => {
    if (!user?.email) return;
    try {
      setLoading(true);
      await FrameworkService.createFramework(
        user.email,
        {
          title: formData.title,
          description: formData.description,
          content: selectedFramework?.content || "",
          isPublic: formData.isPublic,
          tags: formData.tags.split(",").map((t) => t.trim()),
        },
        selectedFramework?.content || "",
      );
      await loadUserFrameworks();
      setNewFrameworkDialog(false);
      setFormData({ title: "", description: "", isPublic: false, tags: "" });
      alert("Framework created successfully!");
    } catch (err) {
      setError("Failed to create framework");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteFramework = async (framework: DevelopmentFramework) => {
    try {
      setLoading(true);
      await FrameworkService.deleteFramework(framework.frameworkId);
      await loadUserFrameworks();
      setConfirmDeleteOpen(false);
      setSelectedForDelete(null);
      alert("Framework deleted successfully!");
    } catch (err) {
      setError("Failed to delete framework");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  // Keep unused for potential future use with code snippets
  void copyToClipboard;

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 3, fontWeight: "bold" }}>
        Development Framework
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Tabs
        value={tabValue}
        onChange={(e, newValue) => setTabValue(newValue)}
        sx={{ borderBottom: "1px solid", borderColor: "divider", mb: 3 }}
      >
        <Tab
          label="Framework Viewer"
          id="framework-tab-0"
          aria-controls="framework-tabpanel-0"
        />
        <Tab
          label="My Frameworks"
          id="framework-tab-1"
          aria-controls="framework-tabpanel-1"
        />
        <Tab
          label="Public Frameworks"
          id="framework-tab-2"
          aria-controls="framework-tabpanel-2"
        />
      </Tabs>

      {/* Tab 0: Framework Viewer */}
      <TabPanel value={tabValue} index={0}>
        <Grid container spacing={3}>
          {selectedFramework && (
            <>
              <Grid item xs={12}>
                <Paper sx={{ p: 3 }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 2,
                    }}
                  >
                    <Box>
                      <Typography variant="h5" sx={{ fontWeight: "bold" }}>
                        {selectedFramework.title}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="textSecondary"
                        sx={{ mt: 1 }}
                      >
                        {selectedFramework.description}
                      </Typography>
                      {selectedFramework.tags &&
                        selectedFramework.tags.length > 0 && (
                          <Box
                            sx={{
                              mt: 2,
                              display: "flex",
                              gap: 1,
                              flexWrap: "wrap",
                            }}
                          >
                            {selectedFramework.tags.map((tag) => (
                              <Chip
                                key={tag}
                                label={tag}
                                size="small"
                                variant="outlined"
                              />
                            ))}
                          </Box>
                        )}
                    </Box>
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <Button
                        startIcon={<DownloadIcon />}
                        variant="outlined"
                        size="small"
                        onClick={handleExportAsMarkdown}
                      >
                        Markdown
                      </Button>
                      <Button
                        startIcon={<DownloadIcon />}
                        variant="outlined"
                        size="small"
                        onClick={handleExportAsJSON}
                      >
                        JSON
                      </Button>
                      {selectedFramework.userId !== "system" && (
                        <Button
                          startIcon={<EditIcon />}
                          variant="outlined"
                          size="small"
                          onClick={() => {
                            // Edit functionality for framework
                          }}
                        >
                          Edit
                        </Button>
                      )}
                    </Box>
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  <Box sx={{ maxHeight: "600px", overflowY: "auto" }}>
                    <Typography
                      variant="body2"
                      component="pre"
                      sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                    >
                      {selectedFramework.content}
                    </Typography>
                  </Box>
                </Paper>
              </Grid>

              {/* Tech Stacks Section */}
              {selectedFramework.techStacks &&
                selectedFramework.techStacks.length > 0 && (
                  <Grid item xs={12}>
                    <Accordion>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="h6">
                          Tech Stacks ({selectedFramework.techStacks.length})
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Grid container spacing={2}>
                          {selectedFramework.techStacks.map((stack, idx) => (
                            <Grid item xs={12} sm={6} key={idx}>
                              <Card variant="outlined">
                                <CardContent>
                                  <Typography
                                    variant="subtitle1"
                                    sx={{ fontWeight: "bold" }}
                                  >
                                    {stack.name}
                                  </Typography>
                                  <Box sx={{ mt: 2 }}>
                                    <Typography
                                      variant="caption"
                                      sx={{ fontWeight: "bold" }}
                                    >
                                      Packages:
                                    </Typography>
                                    <Box
                                      sx={{
                                        mt: 1,
                                        display: "flex",
                                        gap: 1,
                                        flexWrap: "wrap",
                                      }}
                                    >
                                      {stack.packages.map((pkg) => (
                                        <Chip
                                          key={pkg}
                                          label={pkg}
                                          size="small"
                                        />
                                      ))}
                                    </Box>
                                  </Box>
                                </CardContent>
                              </Card>
                            </Grid>
                          ))}
                        </Grid>
                      </AccordionDetails>
                    </Accordion>
                  </Grid>
                )}

              {/* Architecture Patterns Section */}
              {selectedFramework.architecturePatterns &&
                selectedFramework.architecturePatterns.length > 0 && (
                  <Grid item xs={12}>
                    <Accordion>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="h6">
                          Architecture Patterns (
                          {selectedFramework.architecturePatterns.length})
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Grid container spacing={2}>
                          {selectedFramework.architecturePatterns.map(
                            (pattern, idx) => (
                              <Grid item xs={12} key={idx}>
                                <Card variant="outlined">
                                  <CardContent>
                                    <Typography
                                      variant="subtitle1"
                                      sx={{ fontWeight: "bold" }}
                                    >
                                      {pattern.name}
                                    </Typography>
                                    {pattern.structure && (
                                      <Typography
                                        variant="body2"
                                        component="pre"
                                        sx={{
                                          mt: 2,
                                          whiteSpace: "pre-wrap",
                                          wordBreak: "break-word",
                                          backgroundColor: "action.hover",
                                          p: 1,
                                          borderRadius: 1,
                                        }}
                                      >
                                        {pattern.structure}
                                      </Typography>
                                    )}
                                  </CardContent>
                                </Card>
                              </Grid>
                            ),
                          )}
                        </Grid>
                      </AccordionDetails>
                    </Accordion>
                  </Grid>
                )}

              {/* Workflow Steps Section */}
              {selectedFramework.workflowSteps &&
                selectedFramework.workflowSteps.length > 0 && (
                  <Grid item xs={12}>
                    <Accordion>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="h6">
                          Development Workflow (
                          {selectedFramework.workflowSteps.length} phases)
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Box sx={{ width: "100%" }}>
                          {selectedFramework.workflowSteps.map((step, idx) => (
                            <Box key={idx} sx={{ mb: 3 }}>
                              <Typography
                                variant="subtitle1"
                                sx={{ fontWeight: "bold", mb: 1 }}
                              >
                                {step.phase}
                              </Typography>
                              <Box component="ul" sx={{ pl: 2 }}>
                                {step.tasks.map((task, taskIdx) => (
                                  <li key={taskIdx}>
                                    <Typography variant="body2">
                                      {task}
                                    </Typography>
                                  </li>
                                ))}
                              </Box>
                            </Box>
                          ))}
                        </Box>
                      </AccordionDetails>
                    </Accordion>
                  </Grid>
                )}

              {/* Best Practices Section */}
              {selectedFramework.bestPractices &&
                selectedFramework.bestPractices.length > 0 && (
                  <Grid item xs={12}>
                    <Accordion>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="h6">
                          Best Practices (
                          {selectedFramework.bestPractices.length})
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Grid container spacing={2}>
                          {selectedFramework.bestPractices.map(
                            (category, idx) => (
                              <Grid item xs={12} sm={6} key={idx}>
                                <Card variant="outlined">
                                  <CardContent>
                                    <Typography
                                      variant="subtitle1"
                                      sx={{ fontWeight: "bold", mb: 1 }}
                                    >
                                      {category.category}
                                    </Typography>
                                    <Box component="ul" sx={{ pl: 2 }}>
                                      {category.items.map((item, itemIdx) => (
                                        <li key={itemIdx}>
                                          <Typography variant="body2">
                                            {item}
                                          </Typography>
                                        </li>
                                      ))}
                                    </Box>
                                  </CardContent>
                                </Card>
                              </Grid>
                            ),
                          )}
                        </Grid>
                      </AccordionDetails>
                    </Accordion>
                  </Grid>
                )}
            </>
          )}
        </Grid>
      </TabPanel>

      {/* Tab 1: My Frameworks */}
      <TabPanel value={tabValue} index={1}>
        <Box sx={{ mb: 3 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setNewFrameworkDialog(true)}
          >
            Create New Framework
          </Button>
        </Box>

        {loading ? (
          <CircularProgress />
        ) : frameworks.length === 0 ? (
          <Alert severity="info">
            No frameworks yet. Create one to get started!
          </Alert>
        ) : (
          <Grid container spacing={2}>
            {frameworks.map((framework) => (
              <Grid item xs={12} sm={6} md={4} key={framework.frameworkId}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                      {framework.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      sx={{ mt: 1 }}
                    >
                      {framework.description}
                    </Typography>
                    <Box
                      sx={{
                        mt: 2,
                        display: "flex",
                        gap: 0.5,
                        flexWrap: "wrap",
                      }}
                    >
                      {framework.tags.map((tag) => (
                        <Chip
                          key={tag}
                          label={tag}
                          size="small"
                          variant="outlined"
                        />
                      ))}
                    </Box>
                    <Typography
                      variant="caption"
                      sx={{ display: "block", mt: 1 }}
                    >
                      Forks: {framework.forks} |{" "}
                      {framework.isPublic ? "Public" : "Private"}
                    </Typography>
                  </CardContent>
                  <CardActions>
                    <Button
                      size="small"
                      onClick={() => {
                        setSelectedFramework(framework);
                        setTabValue(0);
                      }}
                    >
                      View
                    </Button>
                    <Button
                      size="small"
                      startIcon={<DeleteIcon />}
                      color="error"
                      onClick={() => {
                        setSelectedForDelete(framework);
                        setConfirmDeleteOpen(true);
                      }}
                    >
                      Delete
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </TabPanel>

      {/* Tab 2: Public Frameworks */}
      <TabPanel value={tabValue} index={2}>
        {loading ? (
          <CircularProgress />
        ) : publicFrameworks.length === 0 ? (
          <Alert severity="info">No public frameworks available yet.</Alert>
        ) : (
          <Grid container spacing={2}>
            {publicFrameworks.map((framework) => (
              <Grid item xs={12} sm={6} md={4} key={framework.frameworkId}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                      {framework.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      sx={{ mt: 1 }}
                    >
                      {framework.description}
                    </Typography>
                    <Box
                      sx={{
                        mt: 2,
                        display: "flex",
                        gap: 0.5,
                        flexWrap: "wrap",
                      }}
                    >
                      {framework.tags.map((tag) => (
                        <Chip
                          key={tag}
                          label={tag}
                          size="small"
                          variant="outlined"
                        />
                      ))}
                    </Box>
                    <Typography
                      variant="caption"
                      sx={{ display: "block", mt: 1 }}
                    >
                      Forks: {framework.forks}
                    </Typography>
                  </CardContent>
                  <CardActions>
                    <Button
                      size="small"
                      onClick={() => {
                        setSelectedFramework(framework);
                        setTabValue(0);
                      }}
                    >
                      View
                    </Button>
                    <Button
                      size="small"
                      startIcon={<GitHubForkIcon />}
                      onClick={() => {
                        setSelectedForFork(framework);
                        setForkDialog(true);
                      }}
                    >
                      Fork
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </TabPanel>

      {/* Fork Dialog */}
      <Dialog open={forkDialog} onClose={() => setForkDialog(false)}>
        <DialogTitle>Fork Framework</DialogTitle>
        <DialogContent>
          <Typography>
            This will create a copy of the framework in your personal frameworks
            collection.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setForkDialog(false)}>Cancel</Button>
          <Button
            onClick={() => selectedForFork && handleFork(selectedForFork)}
            variant="contained"
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : "Fork"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* New Framework Dialog */}
      <Dialog
        open={newFrameworkDialog}
        onClose={() => setNewFrameworkDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create New Framework</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
            <TextField
              label="Title"
              fullWidth
              value={formData.title}
              onChange={(e) =>
                setFormData({ ...formData, title: e.target.value })
              }
            />
            <TextField
              label="Description"
              fullWidth
              multiline
              rows={3}
              value={formData.description}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
            />
            <TextField
              label="Tags (comma-separated)"
              fullWidth
              value={formData.tags}
              onChange={(e) =>
                setFormData({ ...formData, tags: e.target.value })
              }
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.isPublic}
                  onChange={(e) =>
                    setFormData({ ...formData, isPublic: e.target.checked })
                  }
                />
              }
              label="Make Public"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewFrameworkDialog(false)}>Cancel</Button>
          <Button
            onClick={handleCreateFramework}
            variant="contained"
            disabled={loading || !formData.title}
          >
            {loading ? <CircularProgress size={24} /> : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={confirmDeleteOpen}
        onClose={() => setConfirmDeleteOpen(false)}
      >
        <DialogTitle>Delete Framework</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{selectedForDelete?.title}"? This
            action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteOpen(false)}>Cancel</Button>
          <Button
            onClick={() =>
              selectedForDelete && handleDeleteFramework(selectedForDelete)
            }
            variant="contained"
            color="error"
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DevelopmentFrameworkPage;
